import { Suspense, lazy, type ReactNode } from 'react';
import { Navigate, createBrowserRouter, useLocation } from 'react-router-dom';
import { LoginPage } from './features/auth/LoginPage';
import { FriendsPage } from './features/friends/FriendsPage';
import { HomePage } from './features/home/HomePage';
import { useSession } from './state/session';
import { AppShell } from './ui/AppShell';
import { FullScreenSpinner } from './ui/Spinner';

// The 3D pages pull in three / R3F / drei / postprocessing (~1.5 MB). Load them on
// demand so /login, / and /friends only ship the light shell.
const WardrobePage = lazy(() => import('./features/wardrobe/WardrobePage').then((m) => ({ default: m.WardrobePage })));
const ShowPage = lazy(() => import('./features/show/ShowPage').then((m) => ({ default: m.ShowPage })));
const LabPage = lazy(() => import('./features/lab/LabPage').then((m) => ({ default: m.LabPage })));

function Lazy({ label, children }: { label: string; children: ReactNode }) {
  return <Suspense fallback={<FullScreenSpinner label={label} />}>{children}</Suspense>;
}

/** Redirects anonymous users to /login, remembering where they were going. */
function RequireAuth({ children }: { children: ReactNode }) {
  const status = useSession((s) => s.status);
  const location = useLocation();
  if (status === 'loading') return <FullScreenSpinner label="Warming up the runway…" />;
  if (status === 'anon') {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?next=${next}`} replace />;
  }
  return <>{children}</>;
}

function NotFound() {
  return (
    <div className="page page-center">
      <div className="card stack" style={{ maxWidth: 420, textAlign: 'center' }}>
        <h1>Off the runway</h1>
        <p className="muted">That page does not exist.</p>
        <a className="btn btn-primary" href="/">
          Back home
        </a>
      </div>
    </div>
  );
}

/**
 * Route table. Each page lives in its feature directory; owners replace the
 * placeholder components in place without touching this file.
 */
export const router = createBrowserRouter([
  { path: '/login', element: <LoginPage /> },
  {
    element: (
      <RequireAuth>
        <AppShell />
      </RequireAuth>
    ),
    children: [
      { path: '/', element: <HomePage /> },
      {
        path: '/wardrobe',
        element: (
          <Lazy label="Opening the wardrobe…">
            <WardrobePage />
          </Lazy>
        ),
      },
      { path: '/friends', element: <FriendsPage /> },
      {
        path: '/show/:id',
        element: (
          <Lazy label="Setting the stage…">
            <ShowPage />
          </Lazy>
        ),
      },
      {
        path: '/lab',
        element: (
          <Lazy label="Opening the lab…">
            <LabPage />
          </Lazy>
        ),
      },
    ],
  },
  { path: '*', element: <NotFound /> },
]);
